import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, Card, CardContent, Avatar, Grid, Paper, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Snackbar, Alert, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StoreIcon from "@mui/icons-material/Store";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import LabelIcon from "@mui/icons-material/Label";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import BarChartIcon from "@mui/icons-material/BarChart";
import EditIcon from "@mui/icons-material/Edit";

import { getZonalAdmins, updateZonalAdmin } from "../../api/adminApi";
import ScrollToTop from "../../utils/ScrollToTop";

const modernInputStyle = {
    '& .MuiOutlinedInput-root': { borderRadius: '12px' }
};

export default function ZonalAdminHub() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [adminDetails, setAdminDetails] = useState(location.state?.adminDetails || null);

    // 👇 States for the Edit Dialog
    const [openEdit, setOpenEdit] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [form, setForm] = useState({ name: "", email: "", password: "", zone: "" });

    const showMessage = (msg, type = "success") => setSnackbar({ open: true, message: msg, severity: type });

    const fetchAdmin = useCallback(async () => {
        try {
            const res = await getZonalAdmins();
            const found = res.data.find((a) => a.id.toString() === id);
            if (found) {
                setAdminDetails(found);
            }
        } catch (err) {
            console.error("Failed to load admin details", err);
        }
    }, [id]);

    useEffect(() => {
        if (!adminDetails) {
            fetchAdmin();
        }
    }, [adminDetails, fetchAdmin]);

    const handleOpenEdit = () => {
        if (!adminDetails) return;
        setForm({
            name: adminDetails.name || "",
            email: adminDetails.email || "",
            password: "",
            zone: adminDetails.zone || "",
        });
        setOpenEdit(true);
    };

    const handleEdit = async () => {
        try {
            setActionLoading(true);
            await updateZonalAdmin(id, form);
            setOpenEdit(false);
            showMessage("Zonal Admin updated successfully!");
            fetchAdmin(); // Refresh details instantly
        } catch (err) {
            showMessage(err?.response?.data?.error || "Failed to update admin", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const gridItems = [
        { label: "Vendors", icon: <StoreIcon sx={{ fontSize: { xs: 26, sm: 32 }, color: "#577A34" }} />, bg: "#E1EED3", route: "vendors" },
        { label: "Riders", icon: <TwoWheelerIcon sx={{ fontSize: { xs: 26, sm: 32 }, color: "#E85D3F" }} />, bg: "#FFF0EE", route: "riders" },
        { label: "Food Tags", icon: <LabelIcon sx={{ fontSize: { xs: 26, sm: 32 }, color: "#1976D2" }} />, bg: "#E1F0FF", route: "tags" },
        { label: "Coupons", icon: <LocalOfferIcon sx={{ fontSize: { xs: 26, sm: 32 }, color: "#D97706" }} />, bg: "#FFF6DE", route: "coupons" },
        { label: "Finance", icon: <BarChartIcon sx={{ fontSize: { xs: 26, sm: 32 }, color: "#B45309" }} />, bg: "#FEF3C7", route: "finance" },
    ];

    return (
        <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "#FFFDF8", p: { xs: 2, sm: 3 }, pb: { xs: "140px", md: "40px" }, boxSizing: "border-box" }}>
            <ScrollToTop />

            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                <IconButton onClick={() => navigate("/zonal-admins")} sx={{ bgcolor: '#FFFFFF', border: '1px solid #ECE5DA' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" fontWeight="900" color="#3A2C23">Admin Details</Typography>
            </Box>

            <Card elevation={0} sx={{ borderRadius: '24px', border: "1px solid #ECE5DA", mb: 4, bgcolor: "#FFFFFF" }}>
                <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: 2, sm: 3 } }}>
                    <Avatar sx={{ bgcolor: "#FFF6DE", color: "#F29D17", width: { xs: 50, sm: 60 }, height: { xs: 50, sm: 60 }, fontSize: { xs: "1.2rem", sm: "1.5rem" }, fontWeight: 900 }}>
                        {adminDetails?.name ? adminDetails.name.charAt(0).toUpperCase() : "Z"}
                    </Avatar>
                    <Box flex={1}>
                        {adminDetails ? (
                            <>
                                <Typography variant="h6" fontWeight="900" color="#3A2C23" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" }, lineHeight: 1.2, mb: 0.5 }}>{adminDetails.name}</Typography>
                                <Typography variant="body2" color="#6E655E" fontWeight="600" mb={0.5} noWrap>✉ {adminDetails.email}</Typography>
                                <Typography variant="body2" color="#E85D3F" fontWeight="800" noWrap>📍 Zone: {adminDetails.zone}</Typography>
                            </>
                        ) : (
                            <>
                                <Skeleton variant="text" width="60%" height={32} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="50%" height={20} />
                            </>
                        )}
                    </Box>
                    {/* 👇 Wired up the edit button click handler */}
                    <IconButton onClick={handleOpenEdit} sx={{ bgcolor: "#E1EED3", color: "#577A34", '&:hover': { bgcolor: "#CCE3AF" } }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </CardContent>
            </Card>

            <Typography variant="h6" fontWeight="900" color="#3A2C23" mb={2}>Zone Management</Typography>

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {gridItems.map((item, idx) => (
                    <Grid item xs={6} sm={4} md={3} lg={2.4} key={idx}>
                        <Paper
                            elevation={0}
                            onClick={() => navigate(`/zonal-admins/${id}/${item.route}`)}
                            sx={{
                                p: { xs: 2, sm: 3 },
                                borderRadius: '20px',
                                border: '1px solid #ECE5DA',
                                textAlign: 'center',
                                cursor: 'pointer',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 16px rgba(0,0,0,0.05)' }
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: 50, sm: 60 },
                                    height: { xs: 50, sm: 60 },
                                    mx: "auto",
                                    mb: { xs: 1.5, sm: 2 },
                                    borderRadius: '16px',
                                    bgcolor: item.bg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                {item.icon}
                            </Box>
                            <Typography
                                variant="subtitle1"
                                fontWeight="800"
                                color="#3A2C23"
                                sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, lineHeight: 1.2 }}
                            >
                                {item.label}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* 👇 EDIT DIALOG MODAL */}
            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}>
                <DialogTitle sx={{ fontWeight: "900", color: "#3A2C23" }}>Edit Zonal Admin</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" fullWidth sx={modernInputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        <TextField label="Email" fullWidth sx={modernInputStyle} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        <TextField label="Zone" fullWidth sx={modernInputStyle} value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} />
                        <TextField label="New Password (leave blank to keep current)" type="password" fullWidth sx={modernInputStyle} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenEdit(false)} sx={{ color: "#6E655E", fontWeight: "800" }}>Cancel</Button>
                    <Button variant="contained" disableElevation onClick={handleEdit} disabled={actionLoading} sx={{ borderRadius: "12px", bgcolor: "#F8C332", color: "#3A2C23", fontWeight: "900", "&:hover": { bgcolor: "#F5B71D" } }}>
                        {actionLoading ? <CircularProgress size={24} /> : "Save Changes"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '12px', fontWeight: 'bold' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}